import HTTP from './http'
const BASE_URL = '/companyrolodex'
import removeEmptyAttributes from '@/helpers/removeEmptyAttributes'

import Store from '@/store'
const currentUsersCompanyKey = () =>
  Store.getters['user/accountInfo'].company ? Store.getters['user/accountInfo'].company.companyKey : 0

export default {
  /**
   * Adds a contact to a specific companies contact list
   * @param {Object} contactInfo Contact details
   * @param {String} companyKey Unique ID for the company who should own this contact (defaults to current users company)
   * @param {Array} addressKeys Unique ID for the JobLocationAddress that this contact belongs to
   * @returns {Promise<*>}
   */
  async addContact({ contactInfo, companyKey, addressKeys }) {
    const body = {
      ...contactInfo,
      companyRolodexJobLocationAddressKeys: addressKeys,
      companyKey: companyKey || currentUsersCompanyKey()
    }
    const response = await HTTP.post(`${BASE_URL}/contact`, body)
    return response.data.data
  },

  /**
   * Add a location to the companies location list
   * @param {Object} contactInfo Location details
   * @param {String} companyKey Unique ID for the company who should own this location (defaults to current users company)
   * @returns {Promise<*>}
   */
  async addLocation({ locationInfo, companyKey }) {
    const body = {
      ...locationInfo,
      companyKey: companyKey || currentUsersCompanyKey()
    }
    const response = await HTTP.post(`${BASE_URL}/joblocation`, body)
    return response.data.data
  },

  /**
   * Update an existing location
   * @param {Object} contactInfo Location details
   * @param {String} companyKey Unique ID for the company who should own this location (defaults to current users company)
   * @returns {Promise<*>}
   */
  async updateLocation(locationInfo) {
    const body = {
      ...locationInfo
    }
    const companyRolodexJobLocationKey = locationInfo.companyRolodexJobLocationKey
    const response = await HTTP.put(
      `${BASE_URL}/joblocation?companyRolodexJobLocationKey=${companyRolodexJobLocationKey}`,
      body
    )
    return response.data.data
  },

  async updateContact(contactInfo) {
    const body = {
      ...contactInfo
    }

    const companyRolodexContactKey = contactInfo.companyRolodexContactKey

    const response = await HTTP.put(`${BASE_URL}/contact?companyRolodexContactKey=${companyRolodexContactKey}`, body)
    return response.data.data
  },

  async deleteContactByKey(companyRolodexContactKey) {
    const params = {
      companyRolodexContactKey
    }
    const response = await HTTP.delete(`${BASE_URL}/contact`, { params })
    return response.data.data
  },

  async deleteLocationByKey(companyRolodexJobLocationKey) {
    const params = {
      companyRolodexJobLocationKey
    }
    const response = await HTTP.delete(`${BASE_URL}/joblocation`, { params })
    return response.data.data
  },

  /**
   * Search for a contact with in the companies contact list. Current service implementation will search across all fields
   * @param {String} searchString The search string
   * @param {String} companyKey Unique ID for the company whose contact list to search in (defaults to current users company)
   * @param {String} name String to search for against the "contactName" field
   * @param {String} email String to search for against the "contactEmail" field
   * @param {String} companyName String to search for against the "locationName" field
   * @param {String} city String to search for against the "locationCity" field
   * @param {String} state String to search for against the "locationState" field
   * @param {String} zip String to search for against the "locationZip" field
   * @returns {Promise<*>}
   */
  async contactSearch({ searchString, companyKey, name, email, companyName, city, state, zip }) {
    let params = {
      companyKey: companyKey || currentUsersCompanyKey(),
      search: searchString,
      searchContactFullName: name,
      searchContactEmailAddress: email,
      searchJobLocationName: companyName,
      searchJobLocationAddressCity: city,
      searchJobLocationAddressState: state,
      searchJobLocationAddressZip: zip
    }
    params = removeEmptyAttributes(params)

    const response = await HTTP.get(`${BASE_URL}/contact/search`, { params })
    return response.data.data
  },

  /**
   * Search for a carrier that was previously used by this company. Returns a max of 50 results.
   * @param {String} searchString The search string
   * @param {String} companyKey Unique ID for the company whose carrier list to search in (defaults to current users company)
   * @returns {Promise<*>}
   */
  async carrierSearch({ searchString, companyKey }) {
    let params = {
      companyKey: companyKey || currentUsersCompanyKey(),
      search: searchString
    }
    params = removeEmptyAttributes(params)

    const response = await HTTP.get(`${BASE_URL}/favorite/carrier/search`, { params })
    return response.data.data.slice(0, 50)
  },

  /**
   * Search for a contact with in the companies location list. Current service implementation will search across all fields
   * @param {String} companyKey Unique ID for the company whose location list to search in (defaults to current users company)
   * @param {String} name String to search for against the "locationName" field
   * @param {String} city String to search for against the "locationCity" field
   * @param {String} state String to search for against the "locationState" field
   * @param {String} zip String to search for against the "locationZip" field
   * @returns {Promise<*>}
   */
  async locationSearch({ companyKey, name, city, state, zip }) {
    let params = {
      companyKey: companyKey || currentUsersCompanyKey(),
      searchJobLocationName: name,
      searchJobLocationAddressCity: city,
      searchJobLocationAddressState: state,
      searchJobLocationAddressZip: zip
    }

    params = removeEmptyAttributes(params)

    const response = await HTTP.get(`${BASE_URL}/joblocation/search`, { params })
    return response.data.data.companyRolodexJobLocation
  },

  async getContactByKey({ companyRolodexContactKey, companyKey }) {
    const params = {
      companyKey: companyKey || currentUsersCompanyKey(),
      companyRolodexContactKey
    }
    const response = await HTTP.get(`${BASE_URL}/contact`, { params })
    return response.data.data
  },

  async getLocationByKey({ companyRolodexJobLocationKey, companyKey }) {
    const params = {
      companyKey: companyKey || currentUsersCompanyKey(),
      companyRolodexJobLocationKey
    }

    if (!companyRolodexJobLocationKey) {
      return []
    }

    const response = await HTTP.get(`${BASE_URL}/joblocation`, { params })
    return response.data.data
  },

  async getLocationByAddressKey({ companyRolodexJobLocationAddressKey, companyKey }) {
    const params = {
      companyKey: companyKey || currentUsersCompanyKey(),
      companyRolodexJobLocationAddressKey
    }
    if (!companyRolodexJobLocationAddressKey) {
      return []
    }
    const response = await HTTP.get(`${BASE_URL}/joblocation`, { params })
    return response.data.data
  }
}
